import React from 'react';
import {useLocation} from 'react-router-dom';
import {styled, Box} from '@mui/material';
import {
  Icon,
  Label,
  designSystemToken,
  Tooltip,
  IconName,
} from '@lightricks/react-design-system';
import NavLink from '@contentlab/components/nav-link';
import {media} from '@contentlab/styles/mediaQueries';

export interface MenuItemProps {
  testID?: string;
  iconName: IconName;
  label: string;
  to: string;
  open: boolean;
  isMobile: boolean;
  onClick?: (menuItem: MenuItemProps) => void;
  matchPaths?: string[];
  disabled?: boolean;
  tooltip?: string;
}

const StyledNavLink = styled(NavLink)({
  [media.mobile]: {
    // width: 'unset !important',
  },
});

const shouldNotForwardProps = ['active'];

const shouldNotForwardPropsExtended = [...shouldNotForwardProps, 'disabled'];

const ItemContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !shouldNotForwardPropsExtended.includes(prop),
})<{active?: boolean; disabled?: boolean}>(({active, disabled}) => ({
  'height': '44px',
  'boxSizing': 'border-box',
  'display': 'flex',
  'alignItems': 'center',
  'gap': '16px',
  'width': '100%',
  'padding': '10px 14px',
  'borderRadius': '8px',
  'background': active
    ? designSystemToken('semantic.bg.neutral-secondary')
    : 'transparent',
  'cursor': disabled ? 'not-allowed' : 'pointer',
  'opacity': disabled ? 0.6 : 1,
  'transition': 'background 0.2s, opacity 0.2s',
  'textDecoration': 'none',
  '&:hover': {
    background: disabled
      ? 'transparent'
      : designSystemToken('semantic.bg.neutral-inverse'),
  },
  [media.mobile]: {
    justifyContent: 'center',
  },
}));

const IconWrapper = styled(Box)({
  width: '24px',
  minWidth: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const StyledLabel = styled(Label)<{open: boolean}>(({open}) => ({
  maxWidth: open ? 160 : 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  opacity: open ? 1 : 0,
  transition: `max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s`,
}));

function MenuItem(props: MenuItemProps) {
  const {
    testID = 'menu-item',
    iconName,
    label,
    to,
    open,
    isMobile,
    onClick,
    matchPaths,
    disabled = false,
    tooltip,
  } = props;

  const location = useLocation();

  const isActive = React.useMemo(() => {
    if (location.pathname === to) {
      return true;
    }
    if (matchPaths) {
      return matchPaths.some((path) => location.pathname.startsWith(path));
    }
    return false;
  }, [location.pathname, to, matchPaths]);

  const menuItemContent = (
    <ItemContainer data-testid={testID} active={isActive} disabled={disabled}>
      <IconWrapper>
        <Icon
          name={iconName}
          size="medium"
          appearance={isActive ? 'primary' : 'neutral'}
          color={
            disabled ? designSystemToken('semantic.fg.disabled') : undefined
          }
        />
      </IconWrapper>
      {!isMobile ? (
        <StyledLabel
          open={open}
          size="md"
          color={
            disabled
              ? designSystemToken('semantic.fg.disabled')
              : designSystemToken('semantic.fg.primary')
          }
          sx={{
            fontWeight: isActive ? 600 : 400,
          }}
        >
          {label}
        </StyledLabel>
      ) : null}
    </ItemContainer>
  );

  const tooltipContent = disabled && tooltip ? tooltip : label;

  const wrappedContent = (
    <Tooltip
      title={tooltipContent}
      arrow
      type="light"
      placement="right"
      disableHoverListener={disabled && tooltip ? false : open}
      slotProps={{
        tooltip: {
          sx: {
            fontSize: '14px',
          },
        },
      }}
    >
      {menuItemContent}
    </Tooltip>
  );

  if (disabled) {
    return (
      <div style={{textDecoration: 'none', width: '100%'}}>
        {wrappedContent}
      </div>
    );
  }

  return (
    <StyledNavLink
      to={to}
      style={{textDecoration: 'none', width: '100%'}}
      end
      onClick={() => onClick?.(props)}
    >
      {() => wrappedContent}
    </StyledNavLink>
  );
}

export default MenuItem;
